import {
  isTaxCalculationOutput,
  UserTaxCalculation,
} from '@/features/admin/user-tax-calculations/userTaxCalculation.slice'
import { NO_STATE_QUARTERLY_TAX_ESTIMATES } from '@/features/taxes/taxConstants'

export interface StateBreakdownWithholding {
  entryInCents: number | null
  spouseEntryInCents: number | null
  projectedInCents: number
  projectedSpouseInCents: number
  annualTotalInCents: number
}

export interface StateBreakdownData {
  state: string
  isHomeState: boolean
  isNoIncomeTaxState: boolean
  /** Whether the customer files in states beyond the home state. */
  hasOtherStates: boolean
  /** Null when the apportionment base predates being persisted; never derived by division. */
  apportionment: { percentage: number; baseInCents: number } | null
  incomeInCents: number | null
  stateStandardDeductionInCents: number | null
  taxableIncomeInCents: number | null
  annualLiabilityInCents: number | null
  ytdDueInCents: number | null
  /** Null for secondary states, whose withholding is credited to the home state. */
  withholding: StateBreakdownWithholding | null
  creditedPaymentsInCents: number | null
  allQuarterPaymentsLogged: boolean | undefined
  quarterEstimateInCents: number | null
}

/**
 * Normalizes everything the state breakdown page renders into one shape, so
 * home-state, secondary-state, and pre-change calculations differ in data
 * rather than in branching inside the page. Fields the calculation never
 * persisted come back null and their rows are omitted rather than guessed at.
 */
export const buildStateBreakdownData = (
  taxCalc: UserTaxCalculation | undefined,
  requestedState: string | undefined
): StateBreakdownData | null => {
  const output = taxCalc?.taxCalculationOutput
  if (!taxCalc || !isTaxCalculationOutput(output)) {
    return null
  }

  const requested = requestedState?.toUpperCase()
  const secondaryStates = output.multiState?.secondaryStates ?? []
  const hasOtherStates = secondaryStates.length > 0
  const secondary = requested
    ? secondaryStates.find((entry) => entry.state.toUpperCase() === requested)
    : undefined

  if (secondary) {
    const baseInCents = output.multiState?.apportionmentBaseIncomeInCents
    return {
      state: secondary.state.toUpperCase(),
      isHomeState: false,
      isNoIncomeTaxState: NO_STATE_QUARTERLY_TAX_ESTIMATES.includes(
        secondary.state.toUpperCase()
      ),
      hasOtherStates,
      apportionment:
        baseInCents === undefined
          ? null
          : { percentage: secondary.percentage, baseInCents },
      incomeInCents: secondary.annualApportionedIncomeInCents,
      stateStandardDeductionInCents:
        secondary.stateStandardDeductionInCents ?? null,
      taxableIncomeInCents: secondary.annualStateTaxableIncomeInCents ?? null,
      annualLiabilityInCents: secondary.annualStateTaxLiabilityInCents,
      ytdDueInCents: secondary.YTDGrossStateTaxLiabilityInCents ?? null,
      withholding: null,
      creditedPaymentsInCents: secondary.creditedPaymentsInCents ?? null,
      allQuarterPaymentsLogged: secondary.allQuarterPaymentsLogged,
      quarterEstimateInCents: secondary.quarterStateEstimateInCents,
    }
  }

  const homeState = taxCalc.homeState?.toUpperCase()
  if (!homeState) {
    return null
  }

  return {
    state: homeState,
    isHomeState: true,
    isNoIncomeTaxState: NO_STATE_QUARTERLY_TAX_ESTIMATES.includes(homeState),
    hasOtherStates,
    apportionment: null,
    incomeInCents: output.data.annualGrossIncomeInCents,
    stateStandardDeductionInCents:
      output.data.stateStandardDeductionInCents ?? null,
    taxableIncomeInCents: output.data.annualStateTaxableIncomeInCents ?? null,
    annualLiabilityInCents: output.data.annualStateTaxLiabilityInCents,
    ytdDueInCents: output.data.YTDGrossStateTaxLiabilityInCents,
    withholding: {
      entryInCents: taxCalc.stateWithholdingInCents ?? null,
      spouseEntryInCents: taxCalc.spouseStateWithholdingInCents ?? null,
      projectedInCents: output.data.projectedStateWithholdingInCents,
      projectedSpouseInCents:
        output.data.projectedSpouseStateWithholdingInCents,
      annualTotalInCents: output.data.annualTotalStateWithholdingInCents,
    },
    creditedPaymentsInCents: output.data.YTDStateTaxPaymentsMadeInCents,
    allQuarterPaymentsLogged: output.data.allStateQuarterPaymentsLogged,
    quarterEstimateInCents: output.data.quarterStateEstimateInCents,
  }
}
